import { Link, useRouterState } from '@tanstack/react-router'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useMe } from '@/api/auth'
import { visibleNav, type NavItem } from './nav'
import { Logo } from './Logo'
import { Tooltip, Kbd } from '@/components/ui/misc'
import { toggleSidebar, ui, useUI } from '@/lib/store'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'

/** data-tour anchor of a sidebar item (web/src/tour): nav-<NavItem.id>. */
export function navTourId(id: string) {
  return `nav-${id}`
}

export function isActive(item: NavItem, path: string) {
  if (item.to === '/') return path === '/'
  return path === item.to || path.startsWith(item.to + '/')
}

/** The matching item with the longest path wins (/process/calendar over /process). */
export function activeNavId(items: NavItem[], path: string): string | undefined {
  let best: NavItem | undefined
  for (const i of items) if (isActive(i, path) && (!best || i.to.length > best.to.length)) best = i
  return best?.id
}

export function SidebarNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useI18n()
  const me = useMe().data
  const path = useRouterState({ select: (s) => s.location.pathname })
  const sections = visibleNav(me)
  const activeId = activeNavId(
    sections.flatMap((s) => s.items),
    path,
  )
  return (
    <nav aria-label={t('nav.main')} className="flex flex-1 flex-col gap-4 overflow-y-auto px-2 py-2 scrollbar-thin" data-tour="nav">
      {sections.map((s) => (
        <div key={s.id} className="flex flex-col gap-0.5">
          {s.label && !collapsed && <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{t(s.label)}</div>}
          {s.label && collapsed && <div className="mx-3 my-2 h-px bg-border" />}
          {s.items.map((item) => {
            const active = item.id === activeId
            const Icon = item.icon
            const link = (
              <Link
                key={item.id}
                to={item.to}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                data-nav={item.id}
                data-tour={navTourId(item.id)}
                className={cn(
                  'group flex h-9 items-center gap-3 rounded-[8px] px-3 text-[13.5px] font-medium transition-colors',
                  active ? 'bg-brand-soft text-brand-soft-ink' : 'text-ink hover:bg-sunken',
                  collapsed && 'justify-center px-0',
                )}
              >
                <Icon className={cn('size-[18px] shrink-0', active ? 'text-brand-strong dark:text-brand' : 'text-muted group-hover:text-ink')} aria-hidden />
                {!collapsed && <span className="truncate">{t(item.label)}</span>}
                {!collapsed && item.key && (
                  <span className="ml-auto hidden text-[11px] text-muted opacity-0 transition-opacity group-hover:opacity-100 xl:inline">
                    G {item.key.toUpperCase()}
                  </span>
                )}
              </Link>
            )
            return collapsed ? (
              <Tooltip key={item.id} content={t(item.label)} side="right">
                {link}
              </Tooltip>
            ) : (
              link
            )
          })}
        </div>
      ))}
    </nav>
  )
}

export function Sidebar() {
  const { t } = useI18n()
  const collapsed = useUI((s) => s.sidebarCollapsed)
  return (
    <aside
      data-print="hide"
      className={cn(
        'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 lg:flex',
        collapsed ? 'w-[64px]' : 'w-[232px]',
      )}
    >
      <div className={cn('flex h-14 items-center border-b border-border', collapsed ? 'justify-center' : 'px-4')}>
        <Link to="/" aria-label="Dovira AI Bridge" className="rounded-md">
          <Logo collapsed={collapsed} />
        </Link>
      </div>
      <SidebarNav collapsed={collapsed} />
      <div className={cn('border-t border-border p-2', collapsed && 'flex justify-center')}>
        <Tooltip content={<span className="flex items-center gap-1.5">{collapsed ? t('nav.expand') : t('nav.collapse')} <Kbd>[</Kbd></span>} side="right">
          <button
            type="button"
            onClick={toggleSidebar}
            className="flex h-8 w-full items-center gap-2 rounded-[8px] px-3 text-[13px] text-muted hover:bg-sunken hover:text-ink"
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
            {!collapsed && t('nav.collapse')}
          </button>
        </Tooltip>
      </div>
    </aside>
  )
}

export function closeMobileNav() {
  ui.set({ mobileNavOpen: false })
}
